import { atomFamily, selectorFamily, GetRecoilValue, useRecoilState, atom } from "recoil";
import { readProfileList } from "../../Data/User/Profile";
import { useCallback } from "react";
import { deepCompareObjects, unique } from "../../Functions";

export const AUTHOR_SUBSCRIPTION = "author-subscription";

const UserKeyList = atom<string[]>({
    key: 'userKeyList',
    default: [],
    dangerouslyAllowMutability: true
});
const UserObject = atom<{[accountid: string]: ProfileType}>({
    key: 'userObject',
    default: {},
    dangerouslyAllowMutability: true
})

// param: "<API's endpoint>"
const userListFamily = atomFamily<{authorId: string}[] | null, string>({
    key: "userList",
    default: null
});

export const usersProfileListFamily = selectorFamily<ProfileType[] | null, string>({
    key: "usersProfileList",
    get: (key: string) => async ({get}: {get: GetRecoilValue}) => {
        const userList: {authorId: string}[] | null = get(userListFamily(key));
        if(userList === null){
            return null;
        } else if(userList.length === 0){
            return [];
        }
        const profileList = await readProfileList(userList.map(author => author.authorId));
        if (profileList && profileList.length > 0){
            return profileList;
        } else {
            throw Error();
        }
    }
});

export const useUsers = () => {
    const [userList, setUserList] = useRecoilState(UserKeyList);
    const [userObject, setUserObject] = useRecoilState(UserObject);
    
    const fetch = useCallback(async (accountidlist: string[]) => {
        let newUserArray: ProfileType[] = [];

        // TODO: fetch newUserArray from server
        newUserArray = await readProfileList(accountidlist) ?? [] as ProfileType[];

        let newUserObject = Object.fromEntries(newUserArray.map(row => ([row.addressid, row])));

        if (! deepCompareObjects(newUserObject, userObject)){
            setUserObject(userObject => ({...userObject, ...newUserObject}));
        }

    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [setUserObject, userObject]);
    
    const refreshUser = useCallback((accountidlist: string[] )=>{
        setUserList(userList => [...userList, ...(accountidlist.filter(id => !!id))].filter(unique))

        fetch(accountidlist);
    },[fetch, setUserList]);

    const secureFetch = useCallback((accountidlist: string[] )=>{
        let realNewList = accountidlist.filter(id => !!id).filter(id => !userList.includes(id));
        
        setUserList(userList => [...userList, ...(accountidlist.filter(id => !!id))].filter(unique))
        if (realNewList.length > 0){
            fetch(realNewList);
        };

    },[fetch, setUserList, userList])

    return {fetch: secureFetch, refreshUser, userObject};
}

export default userListFamily;